// Pure helpers. Nothing here may receive the label → name map except
// `hydrate`, which is display-only: experiments and exports carry labels only.

const LABEL_TOKEN = /\bStudent_[A-Za-z0-9]+\b/g;

export const hydrate = (text, labelMap) =>
  !labelMap || typeof text !== "string"
    ? text
    : text.replace(LABEL_TOKEN, (token) => labelMap[token] || token);

// Lesson Report output → the section list ParsedSummary renders, so the
// console reads like the Session Summary console. `h` hydrates for display.
export const reportSections = (output, h = (t) => t) => {
  const students = (ids) => (ids?.length ? ` (${ids.map(h).join(", ")})` : "");
  const bullet = (item) => ({
    type: "bullet", label: `${h(item.headline)}:`, content: `${h(item.body)}${students(item.student_ids)}`,
  });
  const group = (type, title, items) =>
    items?.length ? [{ type, content: title }, ...items.map(bullet)] : [];
  // Partial or older outputs may lack any of these; render what is there.
  const out = output || {};
  const headlines = out.lessonHeadlines || {};
  const standards = out.standards || [];
  const levels = out.collaborationRecommendations || [];
  const extras = out.extras || [];
  const insights = out.additionalInsights || [];
  const extraBullet = (x) => ({ type: "bullet", label: `${x.key}:`, content: `${h(x.value)}${students(x.student_ids)}` });
  const firstIndex = new Map();
  standards.forEach((s, i) => { if (!firstIndex.has(s.standard_code)) firstIndex.set(s.standard_code, i); });
  const unplaced = extras.filter((x) => !firstIndex.has(x.standard_code));

  return [
    { type: "title-main", content: "Lesson Headlines" },
    ...(headlines.mathHeadline
      ? [{ ...bullet(headlines.mathHeadline), label: `Math: ${h(headlines.mathHeadline.headline)}.` }] : []),
    ...(headlines.collaborationHeadline
      ? [{ ...bullet(headlines.collaborationHeadline), label: `Collaboration: ${h(headlines.collaborationHeadline.headline)}.` }]
      : []),
    ...group("title", "Glows", headlines.glows),
    ...group("title", "Grows", headlines.grows),
    ...group("title", "Next Steps", headlines.nextSteps),
    ...(standards.length ? [{ type: "title-main", content: "Standards" }] : []),
    ...standards.flatMap((s, index) => {
      // Only the first block with this code gets its extras, so each appears once.
      const own = firstIndex.get(s.standard_code) === index
        ? extras.filter((x) => x.standard_code === s.standard_code)
        : [];
      return [
        { type: "title", content: `${s.standard_code}: ${h(s.headline)}` },
        ...group("subtitle", "Misconceptions", s.misconceptions),
        ...group("subtitle", "Recommended Next Steps", s.recommendedNextSteps),
        ...(own.length ? [{ type: "subtitle", content: "Extra fields" }, ...own.map(extraBullet)] : []),
      ];
    }),
    ...(levels.length ? [{ type: "title-main", content: "Collaboration Levels" }] : []),
    ...levels.flatMap((c) => group("title", `Level ${c.level}`, c.recommendations)),
    ...(insights.length ? [{ type: "title-main", content: "Additional Insights" }] : []),
    ...insights.map((a) => ({ type: "bullet", label: `${h(a.title)}:`, content: h(a.body) })),
    ...(unplaced.length ? [{ type: "title-main", content: "Extra Fields" }] : []),
    ...unplaced.map(extraBullet),
  ];
};

export const packetOf = (experiment) =>
  experiment?.jobs?.[experiment.packet_job_id]?.result?.packet || null;

export const runOf = (experiment, runJobId) =>
  experiment?.jobs?.[runJobId]?.result?.run || null;

// Every section key across the manifest, in first-seen order: sessions may differ.
export const sectionKeysOf = (manifest) =>
  [...new Set((manifest || []).flatMap((row) => row.sections.map((sec) => sec.key)))];

const cell = (text) => String(text).replace(/\|/g, "\\|");

// Longer than any backtick run inside, so prompts quoting ``` stay intact.
const fence = (body, lang = "") => {
  const longest = Math.max(2, ...((body || "").match(/`+/g) || []).map((m) => m.length));
  const ticks = "`".repeat(longest + 1);
  return `${ticks}${lang}\n${body}\n${ticks}`;
};

const STATE_TEXT = { present: "present", empty: "empty", fetch_failed: "fetch failed", not_available: "n/a" };

// Free-form Markdown goes in as-is; structured output as fenced JSON.
const outputBlock = (run) => (run.output_mode === "freeform"
  ? (run.output_markdown?.trim() ? run.output_markdown : "_(no output)_")
  : fence(JSON.stringify(run.output, null, 2), "json"));

const promptBlock = (text) => (text ? fence(text) : "_Server default._");
const taskBlock = (text) => (text ? fence(text) : "_None: no task text is sent._");

const count = (n) => (n == null ? "—" : n.toLocaleString("en-US"));

// Langfuse metrics as one line, or null unless status is "ready".
export const metricsText = (metrics) => {
  if (metrics?.status !== "ready") return null;
  const retries = Math.max(0, (metrics.model_calls ?? 1) - 1);
  return [
    metrics.cost_usd == null ? "cost —" : `$${metrics.cost_usd.toFixed(4)}`,
    `${count(metrics.input_tokens)} in / ${count(metrics.output_tokens)} out tokens`,
    `Langfuse ${metrics.latency_ms == null ? "—" : `${(metrics.latency_ms / 1000).toFixed(1)} s`}`,
    `${retries} ${retries === 1 ? "retry" : "retries"}`,
  ].join(" · ");
};

// One variant as a hand-off document: the lesson data once, the variant's
// current settings and prompts, then every run of it. Labels only.
export const exportVariantMarkdown = (experiment, label) => {
  const packet = packetOf(experiment);
  const draft = experiment.variants.find((v) => v.label === label) || {};
  const lines = [
    `# Lesson Report variant ${label}: ${experiment.title}`,
    "",
    `- Exported: ${new Date().toISOString()}`,
    `- Students are pseudonymized (Student_A, …); Teacher and Unknown_student are placeholders.`,
  ];

  if (packet) {
    const keys = sectionKeysOf(packet.manifest);
    lines.push(
      `- Packet built: ${packet.built_at} · ~${packet.token_estimate} tokens`,
      `- Standards: ${packet.standard_codes.join(", ") || "—"}`,
      `- Students: ${packet.student_labels.join(", ") || "—"}`,
      "",
      "## Input data (packet)",
      "",
      "### Coverage",
      "",
      `| Session | Started | Standards | ${keys.join(" | ")} |`,
      `|${" --- |".repeat(keys.length + 3)}`,
      ...packet.manifest.map((row) => `| ${cell(row.session_label)} | ${row.started_at || "—"} | `
        + `${cell(row.standard_codes.join(", ") || "—")} | `
        + `${keys.map((key) => STATE_TEXT[row.sections.find((sec) => sec.key === key)?.state] || "n/a").join(" | ")} |`),
      "",
      "### Sections",
      ...packet.sections.flatMap((sec) => ["", `#### ${sec.title} (\`${sec.key}\`)`, "", fence(sec.content)]),
    );
  } else {
    lines.push("", "_No packet yet._");
  }

  lines.push(
    "",
    `## Variant ${label}: current settings`,
    "",
    `- Model: ${draft.provider || "—"} / ${draft.model_name || "provider default"} · temperature ${draft.temperature ?? "default"}`
      + `${draft.thinking_level ? ` · thinking ${draft.thinking_level}` : ""}`,
    `- Sections: ${draft.input_toggles?.join(", ") || "all"}`,
    `- Output: ${draft.output_mode || "structured"}`,
    "",
    "### System prompt",
    "",
    promptBlock(draft.system_prompt),
    "",
    "### Task prompt",
    "",
    taskBlock(draft.user_prompt),
    "",
    `## Runs of variant ${label}`,
  );

  const runs = experiment.runs
    .map((entry, i) => ({ entry, number: i + 1 }))
    .filter(({ entry }) => entry.label === label);
  if (!runs.length) lines.push("", "_Not run yet._");
  runs.forEach(({ entry, number }) => {
    const job = experiment.jobs[entry.job_id];
    const run = runOf(experiment, entry.job_id);
    const sent = entry.variant || {};
    lines.push("", `### Run ${number}`, "");
    if (!run) {
      lines.push(`- Status: ${job?.status || "unknown"}${job?.error ? ` (${job.error})` : ""}`);
      return;
    }
    lines.push(
      `- Model: ${run.provider} / ${run.model_name} · temperature ${run.temperature}`
        + `${run.thinking_level ? ` · thinking ${run.thinking_level}` : ""} · ${run.output_mode || "structured"}`,
      `- Prompt version: ${run.prompt_version} · latency ${run.latency_ms} ms`,
      ...(metricsText(entry.metrics) ? [`- Metrics: ${metricsText(entry.metrics)}`] : []),
      `- Sections sent: ${sent.input_toggles?.join(", ") || "all"}`,
      `- Reviewer call: ${entry.rating?.verdict || "not rated"}${entry.rating?.note ? ` — ${entry.rating.note}` : ""}`,
      "",
      "#### System prompt sent",
      "",
      fence(run.system_prompt),
      "",
      "#### Task prompt sent",
      "",
      taskBlock(sent.user_prompt),
      "",
      "#### Output",
      "",
      outputBlock(run),
    );
  });

  return `${lines.join("\n")}\n`;
};

export const exportMarkdown = (experiment, runEntry) => {
  const run = runOf(experiment, runEntry.job_id);
  const packet = packetOf(experiment);
  const lines = [
    `# Lesson Report run ${run.label}: ${packet?.title || ""}`,
    "",
    `- Model: ${run.provider} / ${run.model_name} (temperature ${run.temperature}` +
      `${run.thinking_level ? `, thinking ${run.thinking_level}` : ""}) · ${run.output_mode || "structured"}`,
    `- Prompt version: ${run.prompt_version}`,
    `- Latency: ${run.latency_ms} ms`,
    ...(metricsText(runEntry.metrics) ? [`- Metrics: ${metricsText(runEntry.metrics)}`] : []),
    ...(runEntry.metrics?.trace_url ? [`- Trace: ${runEntry.metrics.trace_url}`] : []),
    `- Students are pseudonymized (Student_A, …); Teacher and Unknown_student are placeholders.`,
    "",
    "## Input: system prompt",
    "",
    fence(run.system_prompt),
    "",
    "## Input: user message",
    "",
    fence(run.user_message),
    "",
    "## Output",
    "",
    outputBlock(run),
  ];

  if (runEntry.rating?.verdict) {
    lines.push("", `## Reviewer rating: ${runEntry.rating.verdict}`, "", runEntry.rating.note || "");
  }

  return `${lines.join("\n")}\n`;
};

export const downloadFile = (filename, text, type) => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
